import { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Typography,
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { WeatherAlert } from 'types';
import { WeatherAlertForm } from './types';
import { getUnitForMeasurementType } from 'features/weather/utils/dataConverters';
import { Controller, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import {
  getCautionAndWarningFromDBAlert,
  prepareAlertToSaveToDB,
} from './utils/dataConverters';
import { MeasurementData } from 'features/weather/types';
import { useDishes } from 'features/dishes/api/getDishes';
import { useDish } from 'features/dishes/api/getDish';
import { useCreateWeatherAlert } from './api/createWeatherAlert';
import { useUpdateWeatherAlert } from './api/updateWeatherAlert';

interface Props {
  open: boolean;
  handleClose: () => void;
  weatherAlert: WeatherAlert | undefined;
}

const initialFormState: WeatherAlertForm = {
  dish_id: '',
  condition: '',
  warning: '',
  caution: '',
  enabled: true,
};

const WeatherAlertDialog = ({ open, handleClose, weatherAlert }: Props) => {
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm<WeatherAlertForm>({ defaultValues: initialFormState });
  const useImperial = useAppSelector(
    (state) => state.auth.user?.us_customary_units
  );
  const { data: dishes } = useDishes({});
  const { data: selectedDish } = useDish({ dishId: Number(watch('dish_id')) });
  const createAlert = useCreateWeatherAlert({});
  const updateAlert = useUpdateWeatherAlert({});
  const usedMeasurements = selectedDish
    ? selectedDish?.weatherAlerts
        .map((alert) => alert.condition)
        .filter((measurement) => measurement !== weatherAlert?.condition)
    : [];

  useEffect(() => {
    if (weatherAlert) {
      const { warning, caution } = getCautionAndWarningFromDBAlert(
        weatherAlert,
        useImperial
      );
      reset({
        ...weatherAlert,
        warning: warning ? warning : '',
        caution: caution ? caution : '',
      });
    } else reset(initialFormState);
  }, [weatherAlert, open, useImperial, reset]);

  const onSubmit = async () => {
    const alertForm = getValues();
    const conditionToSave = alertForm.condition;
    if (!conditionToSave) return;
    const { warning, ...alertToSave } = prepareAlertToSaveToDB(
      { ...alertForm, condition: conditionToSave },
      useImperial
    );
    if (!warning) return;
    if (!alertForm.id) {
      try {
        await createAlert.mutateAsync({ ...alertToSave, warning });
        reset(initialFormState);
        handleClose();
      } catch (e) {}
    } else {
      try {
        await updateAlert.mutateAsync({
          data: alertToSave,
          alertId: alertForm.id,
        });
        reset(initialFormState);
        handleClose();
      } catch (e) {}
    }
  };

  const handleCancel = () => {
    reset(initialFormState);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>{watch('id') ? 'Edit Alert' : 'New Alert'}</DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={1}
          direction='column'
          style={{ paddingTop: '.4rem' }}
        >
          <Grid item xs>
            <Controller
              name='dish_id'
              control={control}
              rules={{
                required: 'Dish is required.',
              }}
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth>
                  <InputLabel required id='dish-select-label'>
                    Dish
                  </InputLabel>
                  <Select
                    labelId='dish-select-label'
                    label='Dish'
                    id='dish_id'
                    name='dish_id'
                    onChange={onChange}
                    value={value}
                    error={!!errors.dish_id}
                  >
                    {dishes &&
                      dishes?.map((dish) => (
                        <MenuItem key={dish.id} value={dish.id}>
                          {dish.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs>
            <Controller
              name='condition'
              control={control}
              rules={{
                required: 'Measurement is required.',
                validate: (value) =>
                  !usedMeasurements?.includes(value as MeasurementData) ||
                  'A dish can only have one alert per measurement type.',
              }}
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth>
                  <InputLabel required id='measurement-select-label'>
                    Measurement
                  </InputLabel>
                  <Select
                    labelId='measurement-select-label'
                    label='Measurement'
                    id='condition'
                    name='condition'
                    onChange={onChange}
                    value={value}
                    error={!!errors.condition}
                  >
                    <MenuItem value='wind_kph'>Wind Speed</MenuItem>
                    <MenuItem value='gust_kph'>Wind Gust</MenuItem>
                    <MenuItem value='cloud_percent'>
                      Cloud Cover Percentage
                    </MenuItem>
                    <MenuItem value='precip_mm'>Precipitation</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs>
            <Controller
              name='caution'
              control={control}
              rules={{
                required: false,
                validate: (value) =>
                  (value ? Number(value) < watch('warning') : true) ||
                  'Caution threshold must be less than warning threshold.',
              }}
              render={({ field: { onChange, value } }) => (
                <TextField
                  id='caution'
                  label={
                    'Caution Threshold ' +
                    (watch('condition')
                      ? '(' +
                        getUnitForMeasurementType(
                          watch('condition'),
                          useImperial
                        ) +
                        ')'
                      : '')
                  }
                  name='caution'
                  value={value}
                  onChange={onChange}
                  fullWidth
                  error={!!errors.caution}
                  inputProps={{
                    type: 'number',
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs>
            <Controller
              name='warning'
              control={control}
              rules={{ required: 'Warning threshold is required.' }}
              render={({ field: { onChange, value } }) => (
                <TextField
                  id='Warning'
                  label={
                    'Warning Threshold ' +
                    (watch('condition')
                      ? '(' +
                        getUnitForMeasurementType(
                          watch('condition'),
                          useImperial
                        ) +
                        ')'
                      : '')
                  }
                  name='warning'
                  value={value}
                  onChange={onChange}
                  fullWidth
                  inputProps={{ type: 'number' }}
                  required
                  error={!!errors.warning}
                />
              )}
            />
          </Grid>
          <Grid item container xs>
            {Object.keys(errors).map((name) => (
              <ErrorMessage
                key={name}
                errors={errors}
                name={name as keyof WeatherAlertForm}
                render={({ message }) => (
                  <Typography
                    style={{
                      color: 'red',
                      padding: 0,
                      margin: 0,
                      paddingRight: '.5rem',
                    }}
                  >
                    {message}
                  </Typography>
                )}
              />
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color='secondary' variant='contained'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={() => handleSubmit(onSubmit)()}
          variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeatherAlertDialog;
